import React from "react";
import { css } from "@linaria/core";
import { colors, spacings } from "@ui/Theme";
import Table from "@ui/Table";
import type { ColumnMetaData } from "@ui/Table/ColumnMetaData";
import Text from "@ui/Text";
import { FilterLine } from "@ui/IconV2/react";
import useQuery from "../../../api/useQuery";
import t from "../../../i18n";
import { Link } from "react-router";
import { toPath } from "../../../router/helpers/path";

const PROCESS_STATUSES = ["STARTABLE", "PENDING", "RUNNING", "DONE"] as const;
const PROCESS_TYPES = [
  "SIMULATION",
  "DIAGNOSIS",
  "EXTERNAL_GAP",
  "DIRECT_SIMULATION",
  "SIMULATION_MODEL_VALIDATION",
] as const;

type ProcessStatus = (typeof PROCESS_STATUSES)[number];
type ProcessType = (typeof PROCESS_TYPES)[number];
type UpdatedSinceFilterValue =
  | "ANY"
  | "LAST_30_MINUTES"
  | "LAST_6_HOURS"
  | "LAST_DAY"
  | "LAST_WEEK";

interface StatusByTypeRow {
  type: ProcessType;
  countsByStatus: Partial<Record<ProcessStatus, number>>;
}

interface ByProjectRow {
  count: number;
  projectId: string;
  projectName: string;
}

interface ProcessesOverviewTablesProps {
  displayedStatuses: ProcessStatus[];
  displayedTypes: ProcessType[];
  hasProjectFilter: boolean;
  projectNameById: Record<string, string>;
  selectedUpdatedSince: UpdatedSinceFilterValue;
  selectedProjectIds: string[];
  selectedStatuses: ProcessStatus[];
  selectedTypes: ProcessType[];
  setProjectFilterValues: (values: string[]) => void;
}

export default function ProcessesOverviewTables({
  displayedStatuses,
  displayedTypes,
  hasProjectFilter,
  projectNameById,
  selectedUpdatedSince,
  selectedProjectIds,
  selectedStatuses,
  selectedTypes,
  setProjectFilterValues,
}: ProcessesOverviewTablesProps) {
  const overview = useQuery("/processes/overview", {
    body: {
      projectIds: selectedProjectIds,
      types: selectedTypes,
      statuses: selectedStatuses,
      updatedSince: selectedUpdatedSince,
    },
  }).body;

  const countByStatusAndType = React.useMemo(() => {
    const map = new Map<string, number>();
    for (const item of overview.byStatusAndType) {
      map.set(`${item.status}::${item.type}`, item.count);
    }
    return map;
  }, [overview.byStatusAndType]);

  const statusByTypeRows = React.useMemo<StatusByTypeRow[]>(
    () =>
      displayedTypes.map(type => ({
        type,
        countsByStatus: displayedStatuses.reduce<Partial<Record<ProcessStatus, number>>>(
          (acc, status) => {
            acc[status] = countByStatusAndType.get(`${status}::${type}`) ?? 0;
            return acc;
          },
          {},
        ),
      })),
    [countByStatusAndType, displayedStatuses, displayedTypes],
  );

  const statusByTypeMetadata = React.useMemo<
    Record<string, ColumnMetaData<StatusByTypeRow>>
  >(() => {
    const columns: Record<string, ColumnMetaData<StatusByTypeRow>> = {
      type: {
        label: t("administration.processes.filters.type"),
        render: row => row.type,
      },
    };
    for (const status of displayedStatuses) {
      columns[status] = {
        label: status,
        render: row => row.countsByStatus[status] ?? 0,
      };
    }
    return columns;
  }, [displayedStatuses]);

  const byProjectRows = React.useMemo<ByProjectRow[]>(
    () =>
      overview.byProject.map(item => ({
        ...item,
        projectName:
          projectNameById[item.projectId] ?? t("administration.processes.project_name.unknown"),
      })),
    [overview.byProject, projectNameById],
  );

  const byProjectMetadata = React.useMemo<Record<string, ColumnMetaData<ByProjectRow>>>(
    () => ({
      projectName: {
        label: t("administration.processes.overview.project"),
        render: row => (
          <div className={projectNameCell}>
            {!hasProjectFilter && (
              <button
                type="button"
                className={projectFilterButton}
                title={t("administration.processes.filters.project.filter_this")}
                onClick={() => setProjectFilterValues([row.projectId])}
              >
                <FilterLine size="xs" color={colors.titanium700} />
              </button>
            )}
            <Link
              to={toPath("/projects/:projectId", { projectId: row.projectId })}
              title={row.projectName}
              className={projectLink}
            >
              {row.projectName}
            </Link>
          </div>
        ),
      },
      count: {
        label: t("administration.processes.overview.count"),
        render: row => row.count,
        maxWidth: "160px",
      },
    }),
    [hasProjectFilter, setProjectFilterValues],
  );

  return (
    <div className={overviewGrid}>
      <div className={tableWrapper}>
        <Text type="body1SemiBold">{t("administration.processes.overview.by_status_type")}</Text>
        {statusByTypeRows.length === 0 ? (
          <Text type="body2">{t("administration.processes.empty")}</Text>
        ) : (
          <Table<StatusByTypeRow, string>
            className={statusByTypeTable}
            datas={statusByTypeRows}
            metadata={statusByTypeMetadata}
            computeRowKey={row => row.type}
          />
        )}
      </div>
      <div className={tableWrapper}>
        <Text type="body1SemiBold">{t("administration.processes.overview.by_project")}</Text>
        <div className={projectTableScroll}>
          {byProjectRows.length === 0 ? (
            <Text type="body2">{t("administration.processes.empty")}</Text>
          ) : (
            <Table<ByProjectRow, string>
              datas={byProjectRows}
              metadata={byProjectMetadata}
              computeRowKey={row => row.projectId}
            />
          )}
        </div>
      </div>
    </div>
  );
}

const overviewGrid = css`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${spacings.m};

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const tableWrapper = css`
  display: flex;
  flex-direction: column;
  gap: ${spacings.xs};
`;

const statusByTypeTable = css`
  tr > td:first-child {
    background-color: ${colors.titanium50};
  }
`;

const projectTableScroll = css`
  max-height: 70vh;
  overflow: auto;
`;

const projectLink = css`
  color: inherit;
  text-decoration: underline;
`;

const projectNameCell = css`
  display: inline-flex;
  align-items: center;
  gap: ${spacings.xxs};
`;

const projectFilterButton = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
`;
